import type { HostSanitizationState } from '@playcastdotio/CoreTypes';
import type { MatchmakeableBlockReason } from '@playcastdotio/Messaging';

export type SanitizationHealth = {
  isBetaHost: boolean;
  sanitized: boolean;
  failureCode: string;
  blockReason: MatchmakeableBlockReason | null;
  // Observe-only telemetry. `undefined` means the replicated state did not carry
  // these fields (e.g. the store was cleared to `{}` for an in-flight refresh).
  // Consumers MUST treat `undefined` as "unknown / preserve last known" rather
  // than coercing to 0, otherwise a refresh would prematurely report 0 violations.
  // A genuine reply always carries them (C++ ToJson always serializes the count).
  processPolicyViolationCount?: number;
  lastBlockedProcessName?: string;
  lastBlockedReason?: string;
};

export type SanitizationHealthListener = (health: SanitizationHealth) => void;

export interface SanitizationHealthSource {
  getHealth(): SanitizationHealth;
  onHealthChange(listener: SanitizationHealthListener): () => void;
}

const FAILURE_CODE_TO_BLOCK_REASON: Record<string, MatchmakeableBlockReason> = {
  process_policy: 'process_policy_violation',
  virtual_display: 'virtual_display_unhealthy',
  physical_isolation: 'physical_isolation_unhealthy',
  multiple: 'sanitization_failure',
};

export function mapSanitizationFailureToBlockReason(failureCode: string): MatchmakeableBlockReason | null {
  if (!failureCode || failureCode === 'none') return null;
  return FAILURE_CODE_TO_BLOCK_REASON[failureCode] ?? 'sanitization_failure';
}

type SanitizationStateLike = Partial<HostSanitizationState> | undefined;
type GetSanitizationStateFn = () => SanitizationStateLike;

export class NonsoleSanitizationMonitor implements SanitizationHealthSource {
  private readonly listeners = new Set<SanitizationHealthListener>();
  private lastNotifiedHealth: SanitizationHealth | null = null;

  constructor(private readonly getSanitizationState: GetSanitizationStateFn) {}

  public getHealth(): SanitizationHealth {
    return this.computeHealth();
  }

  public onHealthChange(listener: SanitizationHealthListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public notifyIfChanged(): void {
    const health = this.computeHealth();
    if (this.healthEquals(this.lastNotifiedHealth, health)) return;
    this.lastNotifiedHealth = health;
    for (const listener of this.listeners) {
      try {
        listener(health);
      } catch (err) {
        console.error('[NonsoleSanitizationMonitor] health listener threw', err);
      }
    }
  }

  private computeHealth(): SanitizationHealth {
    const state = this.getSanitizationState();
    const isBetaHost = state?.isBetaHost === true;
    // Fail-closed for a host the payload identifies as beta: it counts as sanitized
    // ONLY when the replicated state explicitly says so. A partial payload (e.g. an
    // `isBetaHost: true` snapshot that has not yet carried a `sanitized` value)
    // must NOT open the gate. Non-beta / not-yet-classified hosts keep the safe
    // default (treated sanitized) so they are never wrongly blocked while the first
    // full payload is in flight.
    const sanitized = isBetaHost ? state?.sanitized === true : state?.sanitized !== false;
    const failureCode = state?.failureCode ?? 'none';
    const blockReason =
      isBetaHost && !sanitized ? mapSanitizationFailureToBlockReason(failureCode) : null;

    return {
      isBetaHost,
      sanitized,
      failureCode,
      blockReason,
      // Leave undefined (not 0/'') when absent so a cleared refresh state does not
      // masquerade as "zero violations" and prematurely reset the persisted count.
      processPolicyViolationCount: state?.processPolicyViolationCount,
      lastBlockedProcessName: state?.lastBlockedProcessName,
      lastBlockedReason: state?.lastBlockedReason,
    };
  }

  private healthEquals(a: SanitizationHealth | null, b: SanitizationHealth): boolean {
    if (!a) return false;
    return (
      a.isBetaHost === b.isBetaHost &&
      a.sanitized === b.sanitized &&
      a.failureCode === b.failureCode &&
      a.blockReason === b.blockReason &&
      a.processPolicyViolationCount === b.processPolicyViolationCount &&
      a.lastBlockedProcessName === b.lastBlockedProcessName &&
      a.lastBlockedReason === b.lastBlockedReason
    );
  }
}
